// The classify behavior's pure core: the job, the prompt, the parse and the
// behavior's own switches. One copy for both hosts of the runner
// (SPEC-custom-agent-template.md §4: "one runner, two hosts"):
//   browser — src/lib/llmAirTrafficControllerService.ts (re-exports this)
//   server  — supabase/functions/ambient/lib/classifyServer.ts
//
// Given an item's title and description, a classify agent:
// 1. Assigns the best-fit item category (problem / opportunity / risk /
//    solution / commitment / investment)
// 2. Answers the three altitude scoping questions for that category (time
//    horizon, work size, impact surface) — each scored 1–5
// 3. Returns a recommended altitude level (1–5), an execution profile and a
//    rationale, for a person to accept, partly accept or reject.

import {
  computeAltitudeFromResponses,
  getAltitudeQuestionSet,
  type AltitudeLevel,
  type AltitudeQuestionResponse,
  type ItemLanguageType,
} from './altitudeQuestionsCore.ts';
import { buildInstructionStack, type AgentCard } from './instructionStackCore.ts';
import { fenceReferenceDocs } from './referenceDocFence.ts';
import { LANGUAGE_NAMES } from './estimateValueCore.ts';

// ── The job (canon doc 20) ────────────────────────────────────────────────────

/** The contract the model works to — the appendix of the instruction stack. */
export const CLASSIFY_SYSTEM_PROMPT =
  'You are the Air Traffic Controller for a prioritization system. ' +
  'You classify items by category and altitude. Respond with valid JSON only.';

export const CLASSIFY_JOB = {
  capabilityId: 'atc',
  role: 'Classify one item by category, altitude and execution profile, for a person to review.',
  goal:
    'Give every new item a defensible category and altitude before anyone has to sort it by hand, ' +
    'so items arrive at sessions comparable with their peers.',
  outputContract:
    'Per item: exactly one classification proposal — category, the three altitude answers with the ' +
    'altitude they compute to, an execution profile, a rationale and a confidence. Nothing else.',
  skillStack: ['Category assignment', 'Altitude scoping', 'Execution profiling', 'Rationale compression'],
  reads: [
    'the item\'s title and description',
    'the category definitions and altitude questions',
    'reference documents an administrator added',
  ],
  proposes: ['one classification proposal: category, altitude answers, execution profile'],
  /** The platform bound on the model parameters; an administrator may lower them (§2 H). */
  modelDefaults: { temperature: 0.2, maxTokens: 800 },
} as const;

export function classifyCard(): AgentCard {
  return {
    capabilityId: CLASSIFY_JOB.capabilityId,
    role: CLASSIFY_JOB.role,
    goal: CLASSIFY_JOB.goal,
    permissions: { read: [...CLASSIFY_JOB.reads], propose: [...CLASSIFY_JOB.proposes] },
    memory: 'turn',
    skillStack: [...CLASSIFY_JOB.skillStack],
    outputContract: CLASSIFY_JOB.outputContract,
  };
}

/**
 * The system prompt for a classify run: the full instruction stack with the
 * workspace's text as the subordinate overlay — it specializes, it never
 * replaces the contract (§2 H). A language line is added when one is set.
 */
export function buildClassifySystemPrompt(params: {
  overlay?: string | null;
  language?: string | null;
}): { systemPrompt: string; l0Version: string; l1Version: string } {
  const overlay = params.overlay?.trim();
  const effectiveOverlay = overlay && overlay !== CLASSIFY_SYSTEM_PROMPT ? overlay : null;
  const language = params.language && LANGUAGE_NAMES[params.language]
    ? `Write the rationale in ${LANGUAGE_NAMES[params.language]}. Keep category names, labels and JSON keys exactly as given.`
    : null;
  const { systemPrompt, meta } = buildInstructionStack({
    card: classifyCard(),
    capabilityAppendix: [CLASSIFY_SYSTEM_PROMPT, language].filter(Boolean).join('\n'),
    workspaceOverlay: effectiveOverlay,
  });
  return { systemPrompt, l0Version: meta.l0Version, l1Version: meta.l1Version };
}

// ── The behavior's own switches (agent_settings.behavior_config) ─────────────

export interface ClassifyBehaviorConfig {
  /** The least confidence the agent must state before it proposes. */
  minConfidence: 'low' | 'medium' | 'high';
  /** Propose on items a person already classified (otherwise only unclassified items). */
  includeClassified: boolean;
}

export const DEFAULT_CLASSIFY_CONFIG: ClassifyBehaviorConfig = {
  minConfidence: 'low',
  includeClassified: false,
};

export function resolveClassifyConfigFrom(stored: Record<string, unknown> | null | undefined): ClassifyBehaviorConfig {
  const c = stored ?? {};
  return {
    minConfidence: c.minConfidence === 'medium' || c.minConfidence === 'high' ? c.minConfidence : 'low',
    includeClassified: c.includeClassified === true,
  };
}

const CONFIDENCE_RANK = { low: 0, medium: 1, high: 2 } as const;

/** True when a stated confidence meets the agent's floor. */
export function meetsConfidence(stated: 'high' | 'medium' | 'low', floor: ClassifyBehaviorConfig['minConfidence']): boolean {
  return CONFIDENCE_RANK[stated] >= CONFIDENCE_RANK[floor];
}

/** A stated confidence as a number, for decision records and calibration. */
export const CONFIDENCE_SCORE = { low: 0.35, medium: 0.6, high: 0.85 } as const;

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ClassifyReferenceDoc {
  id?: string;
  title: string;
  content: string;
  /** A label for the kind of document (category examples, altitude guidance…). */
  docType?: string;
}

export interface ClassifyInput {
  title: string;
  description?: string | null;
  referenceDocs?: ClassifyReferenceDoc[];
}

export type ExecutionProfile = 'human-led' | 'ai-assisted' | 'ai-executable' | 'hybrid';

export interface ClassifyResult {
  suggestedCategory: ItemLanguageType;
  altitudeLevel: AltitudeLevel;
  /** One response per dimension (timeHorizon, workSize, impactSurface) */
  questionResponses: AltitudeQuestionResponse[];
  /** How this work is expected to be executed in a human+AI organization */
  executionProfile: ExecutionProfile;
  rationale: string;
  confidence: 'high' | 'medium' | 'low';
}

/** The fields a classification proposal stores (propose_item_classification). */
export function classificationFieldsOf(result: ClassifyResult): Record<string, unknown> {
  return {
    category: result.suggestedCategory,
    altitude_level: result.altitudeLevel,
    execution_profile: result.executionProfile,
    question_responses: result.questionResponses,
    rationale: result.rationale,
    confidence: result.confidence,
  };
}

// ── Category definitions (for the prompt) ─────────────────────────────────────

export const CATEGORY_DEFINITIONS: Record<ItemLanguageType, string> = {
  problem: 'Something is wrong that needs fixing — a pain, gap, failure, or degradation.',
  opportunity: 'Something we could gain by taking action — upside potential if we act.',
  risk: 'Something that might go wrong if left unaddressed — a threat or exposure.',
  solution: 'A specific approach, initiative, or implementation we could execute.',
  commitment: 'An external promise we must keep — contractual, regulatory, or to a stakeholder.',
  investment: 'A strategic bet for future value — capability building, platform, or R&D.',
};

const ALL_CATEGORIES = Object.keys(CATEGORY_DEFINITIONS) as ItemLanguageType[];

// ── Execution profile definitions (for the prompt) ────────────────────────────

export const EXECUTION_PROFILE_DEFINITIONS: Record<ExecutionProfile, string> = {
  'human-led':     'Requires human judgment and execution throughout. AI may assist with research or drafting but cannot substitute for the core work.',
  'ai-assisted':   'Human leads and owns outcomes; AI handles discrete subtasks (analysis, drafting, data processing) under human direction.',
  'ai-executable': 'An AI agent can execute end-to-end once authorized by a human. Human reviews the output, not the process.',
  'hybrid':        'Explicit handoff points exist between human and AI execution phases — neither can complete the work alone.',
};

const ALL_EXECUTION_PROFILES = Object.keys(EXECUTION_PROFILE_DEFINITIONS) as ExecutionProfile[];

// ── Prompt builder ────────────────────────────────────────────────────────────

export function buildClassifyPrompt(input: ClassifyInput): string {
  const { title, description, referenceDocs = [] } = input;

  // Category section
  const categorySection = ALL_CATEGORIES.map(
    (c) => `- ${c}: ${CATEGORY_DEFINITIONS[c]}`,
  ).join('\n');

  // Question sets for all categories (so the model knows what options map to what scores)
  const questionSections = ALL_CATEGORIES.map((cat) => {
    const qs = getAltitudeQuestionSet(cat);
    const lines = qs.questions.map((q) => {
      const opts = q.options
        .map((o) => `      ${o.value}="${o.label}" (${o.description})`)
        .join('\n');
      return `    ${q.dimension}: "${q.question}"\n${opts}`;
    });
    return `  ${cat}:\n${lines.join('\n')}`;
  }).join('\n\n');

  // Reference documents an administrator pasted in, fenced as reference material.
  const fenced = fenceReferenceDocs(referenceDocs, 'which category and altitude fit items like this one');
  const refSection = fenced ? `\n\n${fenced}` : '';

  // Item to classify
  const descLine = description?.trim() ? `\nDescription: ${description.trim()}` : '';

  // Execution profile section
  const executionProfileSection = ALL_EXECUTION_PROFILES.map(
    (p) => `- ${p}: ${EXECUTION_PROFILE_DEFINITIONS[p]}`,
  ).join('\n');

  return `You are the Air Traffic Controller for a strategic prioritization system. Your job is to read an item and:
1. Assign the single best-fit item category
2. Answer the three altitude scoping questions for that category (score each 1–5)
3. Compute the altitude level (average of the three scores, rounded to 1–5)
4. Determine the execution profile — how this work will be carried out in a human+AI organization
5. Provide a concise rationale

## Item Categories
${categorySection}

## Altitude Scoping Questions (by category)
Each question is scored 1 (most tactical) → 5 (most strategic).
${questionSections}

## Execution Profiles
Assign the profile that best describes how this item would be executed in an organization with capable AI agents:
${executionProfileSection}${refSection}

## Item to Classify
Title: ${title}${descLine}

## Output Format
Respond with valid JSON only — no markdown, no extra text:
{
  "suggestedCategory": "<one of: problem|opportunity|risk|solution|commitment|investment>",
  "questionResponses": [
    { "dimension": "timeHorizon",    "selectedValue": <1-5>, "mappedValue": "<label from options above>" },
    { "dimension": "workSize",       "selectedValue": <1-5>, "mappedValue": "<label from options above>" },
    { "dimension": "impactSurface",  "selectedValue": <1-5>, "mappedValue": "<label from options above>" }
  ],
  "executionProfile": "<one of: human-led|ai-assisted|ai-executable|hybrid>",
  "rationale": "<2-3 sentences explaining the category choice, altitude reasoning, and execution profile>",
  "confidence": "<high|medium|low>"
}

Choose the category that best captures the item's primary nature and intent. Use the reference context (if provided) to inform your determination. The three question scores must match the chosen category's question set.`;
}

// ── Response parser ───────────────────────────────────────────────────────────

/** Parse the model's reply; null when it is not a usable classification (never guessed). */
export function parseClassifyResponse(content: string): ClassifyResult | null {
  try {
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (!jsonMatch) return null;

    const parsed = JSON.parse(jsonMatch[0]);

    const suggestedCategory = ALL_CATEGORIES.includes(parsed.suggestedCategory as ItemLanguageType)
      ? (parsed.suggestedCategory as ItemLanguageType)
      : null;
    if (!suggestedCategory) return null;

    const rawResponses = Array.isArray(parsed.questionResponses) ? parsed.questionResponses : [];
    const questionResponses: AltitudeQuestionResponse[] = rawResponses
      .filter(
        (r: unknown) =>
          r &&
          typeof r === 'object' &&
          ['timeHorizon', 'workSize', 'impactSurface'].includes(
            (r as { dimension?: string }).dimension ?? '',
          ) &&
          typeof (r as { selectedValue?: unknown }).selectedValue === 'number',
      )
      .map((r: Record<string, unknown>) => ({
        questionId: `${suggestedCategory}_${r.dimension as string}`,
        dimension: r.dimension as AltitudeQuestionResponse['dimension'],
        selectedValue: Math.max(1, Math.min(5, Math.round(r.selectedValue as number))) as
          | 1 | 2 | 3 | 4 | 5,
        mappedValue: ((r.mappedValue as string) ?? '') as AltitudeQuestionResponse['mappedValue'],
      }));

    if (questionResponses.length !== 3) return null;

    const altitudeLevel = computeAltitudeFromResponses(questionResponses);
    const rationale = typeof parsed.rationale === 'string' ? parsed.rationale.trim() : '';
    const confidence: ClassifyResult['confidence'] =
      parsed.confidence === 'high' || parsed.confidence === 'low' ? parsed.confidence : 'medium';
    const executionProfile: ExecutionProfile = ALL_EXECUTION_PROFILES.includes(parsed.executionProfile)
      ? (parsed.executionProfile as ExecutionProfile)
      : 'ai-assisted';

    return { suggestedCategory, altitudeLevel, questionResponses, executionProfile, rationale, confidence };
  } catch {
    return null;
  }
}
